import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import dayjs from 'dayjs';
import {
	Table,
	Button,
	Modal,
	Form,
	Input,
	DatePicker,
	Select,
	message,
	Tag,
	Tooltip,
	Space,
} from 'antd';
import {
	BellOutlined,
	PlusOutlined,
	DeleteOutlined,
	EditOutlined,
	ClockCircleOutlined,
	CheckCircleOutlined,
	ExclamationCircleOutlined,
	SendOutlined,
} from '@ant-design/icons';
import './DashboardNotifications.css';
import { notificationsApi } from '../api/notifications';
import { organizationsApi } from '../api/organizations';

const { TextArea } = Input;
const { Option } = Select;

function DashboardNotifications() {
	const { orgData, userData } = useAuth();
	const [notifications, setNotifications] = useState([]);
	const [teams, setTeams] = useState([]);
	const [loading, setLoading] = useState(false);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [form] = Form.useForm();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [notificationToDelete, setNotificationToDelete] = useState(null);

	useEffect(() => {
		if (userData && orgData?.id) {
			const initializeData = async () => {
				setLoading(true);
				try {
					await Promise.all([fetchTeams(), fetchNotifications()]);
				} catch (error) {
					console.error('Initialization error:', error);
				} finally {
					setLoading(false);
				}
			};

			initializeData();
		}
	}, [userData, orgData?.id]);

	const fetchTeams = async () => {
		if (!orgData?.id) return;
		try {
			const teamsData = await organizationsApi.teams.getAll(orgData.id);
			setTeams(Array.isArray(teamsData.teams) ? teamsData.teams : []);
		} catch (error) {
			console.error('Failed to fetch teams:', error);
			message.error('Failed to load teams');
			setTeams([]);
		}
	};

	const fetchNotifications = async () => {
		if (!orgData?.id) return;
		try {
			const data = await notificationsApi.getAll(orgData.id);
			setNotifications(Array.isArray(data) ? data : []);
		} catch (error) {
			console.error('Failed to fetch notifications:', error);
			setNotifications([]);
		}
	};

	const handleCreateNotification = async (values) => {
		setIsSubmitting(true);
		try {
			const notificationData = {
				...values,
				scheduledFor: values.scheduledFor?.toISOString(),
				organizationId: orgData.id,
				teamId: values.teamId || null,
			};

			await notificationsApi.create(notificationData);
			message.success('Notification created successfully');
			setIsModalVisible(false);
			form.resetFields();
			fetchNotifications();
		} catch (error) {
			console.error('Create notification error:', error);
			message.error(
				error.response?.data?.message || 'Failed to create notification'
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleSendNow = async (notificationId) => {
		try {
			message.loading({
				content: 'Sending notification...',
				key: 'sendNow',
			});
			await notificationsApi.sendNow(notificationId);
			await fetchNotifications();
			message.success({
				content: 'Notification sent successfully',
				key: 'sendNow',
				duration: 2,
			});
		} catch (error) {
			console.error('Failed to send notification:', error);
			message.error({
				content:
					error.response?.data?.message ||
					'Failed to send notification',
				key: 'sendNow',
				duration: 2,
			});
		}
	};

	const columns = [
		{
			title: 'Title',
			dataIndex: 'title',
			key: 'title',
		},
		{
			title: 'Message',
			dataIndex: 'message',
			key: 'message',
			ellipsis: true,
		},
		{
			title: 'Schedule',
			dataIndex: 'scheduledFor',
			key: 'scheduledFor',
			render: (date) =>
				date
					? dayjs(date).format('YYYY-MM-DD HH:mm:ss')
					: 'Send immediately',
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (status) => {
				const statusConfig = {
					scheduled: { color: 'blue', icon: <ClockCircleOutlined /> },
					sent: { color: 'green', icon: <CheckCircleOutlined /> },
					failed: {
						color: 'red',
						icon: <ExclamationCircleOutlined />,
					},
				};

				return (
					<Tag color={statusConfig[status]?.color}>
						{statusConfig[status]?.icon} {status.toUpperCase()}
					</Tag>
				);
			},
		},
		{
			title: 'Actions',
			key: 'actions',
			render: (_, record) => (
				<Space>
					{userData?.accessLevel === 'SuperAdmin' &&
						record.status === 'scheduled' && (
							<Tooltip title='Send Now'>
								<Button
									icon={<SendOutlined />}
									onClick={() => handleSendNow(record.id)}
								/>
							</Tooltip>
						)}
					<Tooltip title='Delete'>
						<Button
							icon={<DeleteOutlined />}
							danger
							onClick={() => handleDelete(record.id)}
						/>
					</Tooltip>
				</Space>
			),
		},
		{
			title: 'Target',
			dataIndex: 'teamId',
			key: 'target',
			render: (teamId) => {
				const team = teams.find((t) => t.id === teamId);
				return team ? team.name : 'All Organization';
			},
		},
	];

	const handleDelete = (notificationId) => {
		setNotificationToDelete(notificationId);
		setIsDeleteModalOpen(true);
	};

	const confirmDelete = async () => {
		try {
			await notificationsApi.delete(notificationToDelete);
			message.success('Notification deleted successfully');
			await fetchNotifications();
			setIsDeleteModalOpen(false);
			setNotificationToDelete(null);
		} catch (error) {
			console.error('Delete notification error:', error);
			message.error(
				error.response?.data?.message || 'Failed to delete notification'
			);
		}
	};

	const cancelDelete = () => {
		setIsDeleteModalOpen(false);
		setNotificationToDelete(null);
	};

	return (
		<DashboardLayout>
			<div className='dashboard-page'>
				<div className='section-header'>
					<h1>Notifications</h1>
					<button
						className='primary-button'
						onClick={() => {
							form.resetFields();
							setIsModalVisible(true);
						}}>
						<PlusOutlined />
						Create Notification
					</button>
				</div>

				<div className='content-section'>
					<Table
						columns={columns}
						dataSource={notifications}
						loading={loading}
						rowKey='id'
					/>
				</div>

				<Modal
					title='Create Notification'
					open={isModalVisible}
					onCancel={() => {
						setIsModalVisible(false);
						form.resetFields();
					}}
					footer={null}
					className='announcement-modal'
					width={600}
					centered>
					<Form
						form={form}
						layout='vertical'
						onFinish={handleCreateNotification}
						className='notification-form'>
						<Form.Item
							name='title'
							label='Title'
							rules={[
								{
									required: true,
									message: 'Please enter a title',
								},
								{
									max: 100,
									message:
										'Title cannot exceed 100 characters',
								},
							]}>
							<Input
								maxLength={100}
								showCount
							/>
						</Form.Item>

						<Form.Item
							name='message'
							label='Message'
							rules={[
								{
									required: true,
									message: 'Please enter a message',
								},
								{
									max: 500,
									message:
										'Message cannot exceed 500 characters',
								},
							]}>
							<TextArea
								rows={4}
								maxLength={500}
								showCount
							/>
						</Form.Item>

						<Form.Item
							name='teamId'
							label='Target Team'
							tooltip='Leave empty to send to entire organization'
							className='help-above'>
							<Select
								allowClear
								placeholder='Select a team'>
								{Array.isArray(teams) &&
									teams.map((team) => (
										<Option
											key={team.id}
											value={team.id}>
											{team.name}
										</Option>
									))}
							</Select>
						</Form.Item>

						<Form.Item
							name='scheduledFor'
							label='Schedule For'
							rules={[
								{
									validator: async (_, value) => {
										if (value && value < dayjs()) {
											throw new Error(
												'Cannot schedule notifications in the past'
											);
										}
									},
								},
							]}>
							<DatePicker
								showTime
								format='YYYY-MM-DD HH:mm:ss'
								style={{ width: '100%' }}
								disabledDate={(current) =>
									current && current < dayjs().startOf('day')
								}
							/>
						</Form.Item>

						<div className='modal-actions'>
							<button
								onClick={() => setIsModalVisible(false)}
								className='modal-button modal-button-secondary'>
								Cancel
							</button>
							<button
								type='submit'
								className='modal-button modal-button-primary'
								disabled={isSubmitting}>
								{isSubmitting ? 'Creating...' : 'Create'}
							</button>
						</div>
					</Form>
				</Modal>

				{isDeleteModalOpen && (
					<div className='announcement-modal-overlay'>
						<div className='announcement-modal delete-modal'>
							<div className='modal-header'>
								<h2 className='modal-title'>
									Delete Notification
								</h2>
							</div>
							<div className='modal-content'>
								<p>
									Are you sure you want to delete this
									notification?
								</p>
								<p className='delete-warning'>
									This action cannot be undone.
								</p>
							</div>
							<div className='modal-actions'>
								<button
									onClick={cancelDelete}
									className='modal-button modal-button-secondary'>
									Cancel
								</button>
								<button
									onClick={confirmDelete}
									className='modal-button modal-button-primary modal-button-danger'>
									Delete
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		</DashboardLayout>
	);
}

export default DashboardNotifications;
